import React from 'react';
import { 
  X, Clock, Award, Users, BookOpen, CheckCircle2, GraduationCap,
  Shield, Briefcase, AlertTriangle, UserCheck, Ban, Building2,
  FileText, Target, TrendingUp, Calendar, Globe
} from 'lucide-react';
import { Button } from './ui/button';

const SUBJECT_ICONS = {
  'Anti Money Laundering': Shield,
  'Governance, Risk & Compliance': Briefcase,
  'Financial Crime Prevention': AlertTriangle,
  'Customer Due Diligence': UserCheck,
  'Managing Sanctions Risk': Ban
};

const LEVEL_INFO = {
  essential: {
    label: 'Essential',
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    description: 'Quick introduction courses for foundational knowledge'
  },
  certificate: {
    label: 'Certificate',
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    description: 'Foundation-level qualification for those new to the field'
  },
  specialist: {
    label: 'Specialist Certificate',
    color: 'bg-[#1EBFB3]/20 text-[#1EBFB3] border-[#1EBFB3]/30',
    description: 'Focused expertise in a specific niche area'
  },
  advanced: {
    label: 'Advanced Certificate',
    color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    description: 'Management-level qualification for experienced professionals'
  },
  diploma: {
    label: 'Diploma',
    color: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    description: 'Strategic leadership qualification for senior professionals'
  },
  practitioner: {
    label: 'Practitioner',
    color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    description: 'Role-specific certification for practitioners'
  },
  postgrad: {
    label: 'Postgraduate Diploma',
    color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    description: 'Executive-level qualification for senior leaders'
  }
};

function formatCurrency(amount, currency = 'ZAR') {
  if (currency === 'GBP') {
    return `£${amount?.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || 0}`;
  }
  return `R${amount?.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || 0}`;
}

export default function CourseDetailModal({ course, onClose, onEnquire }) {
  if (!course) return null;

  const SubjectIcon = SUBJECT_ICONS[course.subject_area] || BookOpen;
  const levelInfo = LEVEL_INFO[course.level] || LEVEL_INFO.certificate;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-slate-900 rounded-2xl max-w-4xl w-full my-8 border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="relative p-6 border-b border-slate-800">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-slate-400" />
          </button>
          
          <div className="flex items-start gap-4 pr-12">
            <div className={`p-3 rounded-xl ${levelInfo.color.split(' ')[0]}`}>
              <SubjectIcon className={`h-8 w-8 ${levelInfo.color.split(' ')[1]}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${levelInfo.color}`}>
                  {levelInfo.label}
                </span>
                {course.pricing?.in_house_available && (
                  <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                    In-House Available
                  </span>
                )}
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">{course.title}</h2>
              <p className="text-slate-400">{course.subject_area}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Provider & QAA Level Banner */}
          <div className="flex flex-wrap items-center gap-3">
            {course.provider && (
              <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white ${course.provider === 'ICITP' ? 'bg-emerald-600' : 'bg-blue-600'}`}>
                Delivered by {course.provider}
              </span>
            )}
            {course.qaa_level && (
              <span className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-700 text-slate-300">
                {course.qaa_level}
              </span>
            )}
            {course.cohort_dates ? (
              <span className="px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-500/20 text-amber-400">
                Cohort dates apply
              </span>
            ) : (
              <span className="px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-500/20 text-emerald-400">
                Start anytime
              </span>
            )}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <Clock className="h-5 w-5 text-amber-500 mb-2" />
              <div className="text-sm text-slate-400">Duration</div>
              <div className="text-white font-medium">{course.duration || '3-6 months'}</div>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <FileText className="h-5 w-5 text-amber-500 mb-2" />
              <div className="text-sm text-slate-400">Delivery</div>
              <div className="text-white font-medium text-sm">{course.delivery_method || 'Flexible learning'}</div>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <Users className="h-5 w-5 text-amber-500 mb-2" />
              <div className="text-sm text-slate-400">Target Audience</div>
              <div className="text-white font-medium">{course.target_roles?.join(', ') || 'All levels'}</div>
            </div>
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
              <Award className="h-5 w-5 text-amber-500 mb-2" />
              <div className="text-sm text-slate-400">Awarded By</div>
              <div className="text-white font-medium text-sm">{course.awarding_body || course.provider || 'ICA'}</div>
            </div>
          </div>

          {/* Lifetime Qualification Notice */}
          <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              <div>
                <div className="text-emerald-400 font-medium">Lifetime Qualification</div>
                <div className="text-sm text-slate-400">This qualification never expires and does not require active membership to maintain.</div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-amber-500" />
              Qualification Overview
            </h3>
            <p className="text-slate-300 leading-relaxed">
              {course.full_description || course.description}
            </p>
          </div>

          {/* Learning Outcomes */}
          {course.learning_outcomes && course.learning_outcomes.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-500" />
                What You Will Learn
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {course.learning_outcomes.map((outcome, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/30">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Syllabus/Modules */}
          {course.syllabus && course.syllabus.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-amber-500" />
                Course Syllabus
              </h3>
              <div className="space-y-2">
                {course.syllabus.map((module, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 font-semibold text-sm">
                      {index + 1}
                    </div>
                    <span className="text-slate-300">{module}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Career Outcomes */}
          {course.career_outcomes && course.career_outcomes.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-amber-500" />
                Career Opportunities
              </h3>
              <div className="flex flex-wrap gap-2">
                {course.career_outcomes.map((career, index) => (
                  <span 
                    key={index} 
                    className="px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-sm"
                  >
                    {career}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Entry Requirements */}
          {course.entry_requirements && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <FileText className="h-5 w-5 text-amber-500" />
                Entry Requirements
              </h3>
              <p className="text-slate-300">{course.entry_requirements}</p>
            </div>
          )}

          {/* Why Navigate Learning */}
          <div className="p-5 rounded-xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/20">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-amber-500" />
              Why Study with Navigate Learning?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                40% discount on ICA direct prices
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                QCTO accredited training provider
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Local South African support
              </div>
              {course.pricing?.in_house_available && (
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  In-house facilitation available
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                UK-aligned international qualification
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Flexible self-paced learning
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Pricing & CTA */}
        <div className="p-6 border-t border-slate-800 bg-slate-800/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-white">
                  {formatCurrency(course.pricing?.total_zar)}
                </span>
                <span className="text-lg text-slate-500 line-through">
                  {formatCurrency(course.pricing?.ica_price_zar)}
                </span>
              </div>
              <div className="text-sm text-slate-400 mt-1">
                {formatCurrency(course.pricing?.total_gbp, 'GBP')} 
                {course.pricing?.membership_required && ' (includes ICA membership)'}
              </div>
              <div className="text-sm text-emerald-400 font-medium mt-1">
                You save {formatCurrency(course.pricing?.savings_zar)} (40% off)
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="border-slate-700 text-slate-300"
              >
                Back to Courses
              </Button>
              <Button
                onClick={() => onEnquire(course)}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8"
              >
                Enquire Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
